import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Brain,
  Target,
  TrendingUp,
  Award,
  BookOpen,
  MessageSquare,
  BarChart3,
  FileText,
  Rocket,
  CheckCircle,
  Clock,
  Zap,
  Download,
  Settings,
  Trash2,
  ArrowRight,
  Search,
  Activity,
  Database,
  Menu,
  X,
  Layout,
  LogOut,
  Home
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { GridBackground, TechCard, TechButton, DataPanel, StatusIndicator } from '../components/TechUI';
import LoadingSpinner from '../components/LoadingSpinner';
import authService from '../firebase/authService';
import { careerAPI, paymentAPI } from '../utils/api';
import ProgressTracker from '../components/ProgressTracker';
import ResumeImprovementModule from '../components/ResumeImprovementModule';
import PortfolioBuilder from '../components/PortfolioBuilder';
import InterviewPrepModule from '../components/InterviewPrepModule';
import AnalyticsDashboard from '../components/AnalyticsDashboard';
import UpgradePrompt from '../components/UpgradePrompt';
import UsageLimitsCard from '../components/UsageLimitsCard';
// New feature components
import SkillRadarChart from '../components/SkillRadarChart';
import SalaryTrajectory from '../components/SalaryTrajectory';
import CareerReadinessScore from '../components/CareerReadinessScore';
import WeeklyChallenges from '../components/WeeklyChallenges';
import AICareerCoach from '../components/AICareerCoach';
import TrendingCareersWidget from '../components/TrendingCareersWidget';
import DaysOnJourney from '../components/DaysOnJourney';
import PeerComparison from '../components/PeerComparison';
import ReferralProgram from '../components/ReferralProgram';
import ConfettiCelebration from '../components/ConfettiCelebration';
import CredentialsDashboard from '../components/CredentialsDashboard';
import ProfileCompletenessWidget from '../components/ProfileCompletenessWidget';

const PremiumDashboardPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('roadmap');
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [roadmaps, setRoadmaps] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [usageStats, setUsageStats] = useState(null);
  const [usageStatsLoading, setUsageStatsLoading] = useState(true);
  const [usageStatsError, setUsageStatsError] = useState(null);
  const [showCelebration, setShowCelebration] = useState(false);

  useEffect(() => {
    checkAccess();
  }, []);

  const checkAccess = async () => {
    try {
      // Check authentication
      const currentUser = authService.getCurrentUser();
      if (!currentUser) {
        toast.error('Please sign in to access dashboard');
        navigate('/');
        return;
      }
      setUser(currentUser);

      // Check subscription status (allow access for all users)
      let userSub = null;

      try {
        const subResponse = await paymentAPI.getUserSubscription();
        userSub = subResponse?.subscription || subResponse;
      } catch (error) {
        console.error('Error fetching subscription:', error);
        userSub = { tier: 'free', status: 'active', limits: {}, usage: {} };
      }

      // Default to free if no subscription found
      if (!userSub) {
        userSub = { tier: 'free', status: 'active', limits: {}, usage: {} };
      }

      setSubscription(userSub);

      // Load user roadmaps
      try {
        const roadmapsResponse = await careerAPI.getUserRoadmaps();
        setRoadmaps(roadmapsResponse.roadmaps || []);
      } catch (error) {
        console.error('Error loading roadmaps:', error);
      }

      // Load analytics
      try {
        const analyticsResponse = await careerAPI.getUserAnalytics();
        setAnalytics(analyticsResponse);
      } catch (error) {
        console.error('Error loading analytics:', error);
      }

      // Load usage stats
      setUsageStatsLoading(true);
      try {
        const usageResponse = await paymentAPI.getUserUsageStats();
        setUsageStats(usageResponse.usage || {});
        setUsageStatsError(null);
      } catch (error) {
        console.error('Error loading usage stats:', error);
        setUsageStatsError('Failed to load usage data');
      } finally {
        setUsageStatsLoading(false);
      }

    } catch (error) {
      console.error('Error loading dashboard:', error);
      toast.error('Failed to load dashboard. Redirecting...');
      navigate('/subscribe');
    } finally {
      setLoading(false);
    }
  };

  const filteredRoadmaps = roadmaps.filter(roadmap => {
    const matchesSearch = roadmap.careerPath?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter =
      filterStatus === 'all' ||
      (filterStatus === 'active' && roadmap.isActive) ||
      (filterStatus === 'completed' && roadmap.status === 'completed');

    return matchesSearch && matchesFilter;
  });

  const handleRoadmapClick = (roadmap) => {
    if (!roadmap.roadmapData) {
      toast.error('Roadmap data is missing. Please regenerate this roadmap.');
      return;
    }

    localStorage.setItem('selectedRoadmapId', roadmap.id);
    localStorage.setItem('roadmapData', JSON.stringify(roadmap.roadmapData));
    localStorage.setItem('selectedCareer', JSON.stringify({ title: roadmap.careerPath }));

    navigate('/roadmap');
  };

  const handleDeleteRoadmap = async (roadmapId, e) => {
    e.stopPropagation();
    if (!window.confirm('Terminate this roadmap?')) return;

    try {
      await careerAPI.deleteRoadmap(roadmapId);
      toast.success('Roadmap terminated');
      // Reload roadmaps
      const roadmapsResponse = await careerAPI.getUserRoadmaps();
      setRoadmaps(roadmapsResponse.roadmaps || []);
    } catch (error) {
      toast.error('Failed to delete roadmap');
    }
  };

  const handleNewPath = () => {
    const isPremiumStatus = subscription && subscription.tier && subscription.tier !== 'free';
    if (isPremiumStatus) {
      localStorage.removeItem('careerSuggestions');
      localStorage.removeItem('roadmapData');
      localStorage.removeItem('selectedCareer');
      navigate('/build-roadmap');
    } else {
      navigate('/free-results');
    }
  };

  // Keyboard shortcuts for tab switching (1-6) + extras
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't trigger in input fields
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

      // Tab switching 1-6
      const tabIndex = parseInt(e.key, 10) - 1;
      if (tabIndex >= 0 && tabIndex < tabs.length) {
        setActiveTab(tabs[tabIndex].id);
        return;
      }

      // '/' to focus search
      if (e.key === '/' && activeTab === 'roadmap') {
        e.preventDefault();
        const searchInput = document.querySelector('input[placeholder*="SEARCH"]');
        if (searchInput) searchInput.focus();
        return;
      }

      // 'n' for new roadmap
      if (e.key === 'n' && !e.ctrlKey && !e.metaKey) {
        handleNewPath();
        return;
      }

      // 'h' for home tab
      if (e.key === 'h' && !e.ctrlKey && !e.metaKey) {
        setActiveTab('home');
        return;
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeTab]);

  const tabs = [
    { id: 'home', name: 'Overview', icon: Home },
    { id: 'roadmap', name: 'Roadmaps', icon: Target },
    { id: 'resume', name: 'Resume AI', icon: FileText },
    { id: 'portfolio', name: 'Portfolio', icon: Rocket },
    { id: 'interview', name: 'Interview Prep', icon: MessageSquare },
    { id: 'credentials', name: 'Credentials', icon: Award },
    { id: 'analytics', name: 'Analytics', icon: BarChart3 }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'home':
        return (
          <div className="space-y-6">
            {/* Days on Journey */}
            <DaysOnJourney roadmaps={roadmaps} />

            {/* Top Row: Career Readiness + Skill Radar */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <CareerReadinessScore roadmaps={roadmaps} analytics={analytics} user={user} />
              <SkillRadarChart roadmaps={roadmaps} analytics={analytics} />
            </div>

            {/* Middle Row: Salary + Weekly Challenges */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <SalaryTrajectory roadmaps={roadmaps} />
              <WeeklyChallenges roadmaps={roadmaps} user={user} />
            </div>

            {/* Bottom Row: Trending + Peer Comparison + Referral */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <TrendingCareersWidget />
              <PeerComparison roadmaps={roadmaps} analytics={analytics} />
              <ReferralProgram user={user} subscription={subscription} />
            </div>
          </div>
        );

      case 'roadmap':
        return (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row gap-4 mb-6 items-center justify-between">
              <div className="relative w-full sm:w-96">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <input
                  type="text"
                  placeholder="SEARCH_PATHS..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-[#0B0F19] border border-white/10 text-slate-200 placeholder-slate-600 focus:outline-none focus:border-emerald-500/50 transition-all text-xs font-mono rounded-lg"
                />
              </div>

              <div className="flex gap-2">
                {['all', 'active', 'completed'].map(status => (
                  <button
                    key={status}
                    onClick={() => setFilterStatus(status)}
                    className={`px-3 py-1.5 rounded text-xs font-mono uppercase transition-colors ${filterStatus === status
                      ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                      : 'text-slate-500 hover:text-slate-300'
                      }`}
                  >
                    {status}
                  </button>
                ))}
              </div>

              <button
                onClick={handleNewPath}
                className="btn-primary text-xs py-2 px-4"
              >
                <span className="mr-2">NEW_PATH</span>
                <ArrowRight className="w-3 h-3 inline" />
              </button>
            </div>

            {filteredRoadmaps.length === 0 ? (
              <div className="text-center py-24 border border-dashed border-white/10 rounded-2xl bg-white/5">
                <Database className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                <h3 className="text-lg font-mono font-bold text-white mb-2 uppercase">No Data Found</h3>
                <p className="text-slate-400 mb-6 text-xs font-mono">Initialize a new career path to get started.</p>
                <button onClick={handleNewPath} className="btn-primary text-xs">
                  CREATE_FIRST_PATH
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {filteredRoadmaps.map((roadmap) => (
                  <div
                    key={roadmap.id}
                    onClick={() => handleRoadmapClick(roadmap)}
                    className="group relative p-6 bg-[#0F131F] border border-white/5 rounded-2xl hover:border-emerald-500/30 transition-all cursor-pointer overflow-hidden"
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />

                    <div className="relative z-10">
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${roadmap.status === 'completed' ? 'bg-emerald-400' :
                            roadmap.isActive ? 'bg-blue-400 animate-pulse' : 'bg-yellow-400'
                            }`} />
                          <span className="text-[10px] font-mono uppercase text-slate-500">
                            {roadmap.status === 'completed' ? 'Complete' :
                              roadmap.isActive ? 'Active' : 'Paused'}
                          </span>
                        </div>
                        <button
                          onClick={(e) => handleDeleteRoadmap(roadmap.id, e)}
                          className="text-slate-600 hover:text-red-400 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>

                      <h3 className="text-lg font-bold text-white mb-4 line-clamp-2 font-display group-hover:text-emerald-400 transition-colors">
                        {roadmap.careerPath}
                      </h3>

                      <div className="space-y-3 mb-6">
                        <div className="flex justify-between text-xs text-slate-400 font-mono">
                          <span>Progress</span>
                          <span>{roadmap.progress?.progressPercentage || 0}%</span>
                        </div>
                        <div className="w-full bg-slate-800 h-1.5 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-emerald-500 transition-all duration-1000"
                            style={{ width: `${roadmap.progress?.progressPercentage || 0}%` }}
                          />
                        </div>
                      </div>

                      <div className="flex items-center justify-between text-xs text-slate-500 font-mono border-t border-white/5 pt-4">
                        <div className="flex gap-3">
                          <span className="flex items-center gap-1">
                            <Target className="w-3 h-3" /> {roadmap.progress?.completedTasks || 0}/{roadmap.progress?.totalTasks || 0}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" /> {roadmap.progress?.completedWeeks || 0}w
                          </span>
                        </div>
                        <ArrowRight className="w-4 h-4 text-emerald-500 -translate-x-2 opacity-0 group-hover:translate-x-0 group-hover:opacity-100 transition-all" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      case 'resume':
        return <ResumeImprovementModule userId={user?.uid} />;

      case 'portfolio':
        return <PortfolioBuilder userId={user?.uid} roadmaps={roadmaps} subscription={subscription} />;

      case 'interview':
        return <InterviewPrepModule userId={user?.uid} roadmaps={roadmaps} />;

      case 'analytics':
        return <AnalyticsDashboard userId={user?.uid} />;

      case 'credentials':
        return <CredentialsDashboard roadmaps={roadmaps} user={user} />;

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0F19] flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  const isPremium = subscription && subscription.tier && subscription.tier !== 'free';

  return (
    <>
      {/* Main Container - Fixed Height "Cockpit" */}
      <div className="h-[calc(100vh-64px)] bg-[#0B0F19] overflow-hidden flex flex-col md:flex-row">

        {/* Mobile Sidebar Toggle */}
        <div className="md:hidden p-4 border-b border-white/10 flex items-center justify-between bg-[#0B0F19]">
          <span className="font-mono font-bold text-white">Dashboard</span>
          <button onClick={() => setIsSidebarOpen(!isSidebarOpen)} className="p-2 text-slate-400">
            {isSidebarOpen ? <X /> : <Menu />}
          </button>
        </div>

        {/* Sidebar Navigation */}
        <AnimatePresence>
          {(isSidebarOpen || window.innerWidth >= 768) && (
            <motion.div
              initial={{ x: -300, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: -300, opacity: 0 }}
              className={`
                fixed md:relative z-20 w-full md:w-72 h-full bg-[#0F131F] border-r border-white/10 flex flex-col
                ${window.innerWidth < 768 ? 'absolute inset-0' : ''}
              `}
            >
              <div className="p-6 border-b border-white/10">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center text-white font-bold text-lg">
                    {user?.displayName?.[0] || 'U'}
                  </div>
                  <div>
                    <div className="text-sm font-bold text-white truncate max-w-[140px]">
                      {user?.displayName || 'User'}
                    </div>
                    <div className={`text-[10px] font-mono uppercase px-2 py-0.5 rounded-full inline-block ${isPremium ? 'bg-emerald-500/20 text-emerald-400' : 'bg-yellow-500/20 text-yellow-400'
                      }`}>
                      {subscription?.tier || 'Free'} Plan
                    </div>
                  </div>
                </div>
                {!isPremium && (
                  <button
                    onClick={() => navigate('/subscribe')}
                    className="w-full py-2 text-xs bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 border border-emerald-500/20 rounded-lg transition-colors font-mono uppercase"
                  >
                    Upgrade Now
                  </button>
                )}

                {/* Usage Limits Card */}
                <UsageLimitsCard
                  usageStats={usageStats}
                  tier={subscription?.tier}
                  onUpgrade={() => navigate('/subscribe')}
                  loading={usageStatsLoading}
                  error={usageStatsError}
                />

                {/* Profile Completeness Widget */}
                <ProfileCompletenessWidget />
              </div>

              <div className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => {
                        setActiveTab(tab.id);
                        if (window.innerWidth < 768) setIsSidebarOpen(false);
                      }}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all group ${isActive
                        ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                        : 'text-slate-400 hover:text-white hover:bg-white/5 border border-transparent'
                        }`}
                    >
                      <Icon className={`w-5 h-5 ${isActive ? 'text-emerald-400' : 'text-slate-500 group-hover:text-white'}`} />
                      <span className="font-medium text-sm">{tab.name}</span>
                      {isActive && <div className="ml-auto w-1.5 h-1.5 rounded-full bg-emerald-400 shadow-[0_0_10px_rgba(52,211,153,0.5)]" />}
                    </button>
                  );
                })}
              </div>

              <div className="p-4 border-t border-white/10 bg-[#0B0F19]/50 space-y-2">
                <button
                  onClick={() => navigate('/build-roadmap')}
                  className="w-full flex items-center gap-3 px-4 py-2 text-slate-400 hover:text-white transition-colors text-sm rounded-lg hover:bg-white/5"
                >
                  <Settings className="w-4 h-4" />
                  <span>Settings</span>
                </button>
                <button
                  onClick={() => {
                    authService.logout();
                    navigate('/');
                  }}
                  className="w-full flex items-center gap-3 px-4 py-2 text-slate-400 hover:text-red-400 transition-colors text-sm rounded-lg hover:bg-red-500/10"
                >
                  <LogOut className="w-4 h-4" />
                  <span>Sign Out</span>
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Main Content Area */}
        <div className="flex-1 overflow-y-auto bg-grid-slate-900/[0.04] relative custom-scrollbar">
          <div className="absolute inset-0 bg-[#0B0F19] -z-10" />
          <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 -z-10" />

          <div className="max-w-7xl mx-auto p-6 md:p-10">
            {/* Breadcrumbs */}
            <div className="flex items-center space-x-2 text-[10px] font-mono text-slate-500 uppercase tracking-widest mb-4">
              <span className="hover:text-slate-300 cursor-pointer" onClick={() => navigate('/dashboard')}>Dashboard</span>
              <span>/</span>
              <span className="text-emerald-400">{tabs.find(t => t.id === activeTab)?.name}</span>
            </div>

            {/* Header with Time-of-Day Greeting */}
            <div className="mb-8">
              <p className="text-slate-500 text-xs font-mono mb-1">
                {(() => {
                  const hour = new Date().getHours();
                  return hour < 12 ? 'Good Morning' : hour < 17 ? 'Good Afternoon' : 'Good Evening';
                })()}, {user?.displayName?.split(' ')[0] || 'there'} 👋
              </p>
              <h1 className="text-3xl font-bold text-white font-display mb-2">
                {tabs.find(t => t.id === activeTab)?.name}
              </h1>
              <p className="text-slate-400 text-sm">
                Manage your {activeTab} and track your progress.
                <span className="text-slate-600 ml-2 hidden md:inline">Press 1-{tabs.length} to switch tabs</span>
              </p>
            </div>

            {/* Content */}
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              {renderTabContent()}
            </motion.div>
          </div>
        </div>
      </div>

      {/* AI Career Coach - Floating Chat */}
      <AICareerCoach user={user} roadmaps={roadmaps} subscription={subscription} />

      {/* Confetti Celebration */}
      <ConfettiCelebration trigger={showCelebration} onComplete={() => setShowCelebration(false)} />
    </>
  );
};

export default PremiumDashboardPage;
